import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

/**
 * Custom hook that encapsulates all task data fetching and state management.
 * Handles tasks, groups, stats, locations, users, and filter state.
 */
export default function useTasksData() {
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('dzis');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('aktywne');
  const [searchQuery, setSearchQuery] = useState('');

  // Location and user state
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showAllTasks, setShowAllTasks] = useState(false);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showQuickTaskModal, setShowQuickTaskModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const { user: authUser } = useAuth();

  // Initialize user and locations
  useEffect(() => {
    if (authUser) {
      setCurrentUser(authUser);
      if (authUser.location_id) {
        setSelectedLocation(authUser.location_id);
      }
      if (authUser.user_type === 'admin') {
        setShowAllTasks(true);
      }
    }

    api.get('/locations/')
      .then(response => {
        const data = response.data;
        if (data.success || Array.isArray(data)) {
          setLocations(data.success ? data.data : data);
        }
      })
      .catch(err => console.error('Error fetching locations:', err));
  }, [authUser]);

  // Fetch users for selected location
  useEffect(() => {
    const url = selectedLocation
      ? `/tasks/users?location_id=${selectedLocation}`
      : `/tasks/users`;

    api.get(url)
      .then(response => {
        if (response.data.success) {
          setUsers(response.data.data || []);
        }
      })
      .catch(err => console.error('Error fetching users:', err));
  }, [selectedLocation]);

  // Main data fetcher
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'wszystkie') {
        params.append('kategoria', selectedCategory);
      }
      if (selectedStatus) {
        params.append('status', selectedStatus);
      }
      if (selectedGroup) {
        params.append('grupa_id', selectedGroup);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      if (selectedLocation) {
        params.append('location_id', selectedLocation);
      }
      if (currentUser?.login) {
        params.append('user_login', currentUser.login);
      }
      if (showAllTasks) {
        params.append('show_all', 'true');
      }

      const groupParams = new URLSearchParams();
      if (selectedLocation) {
        groupParams.append('location_id', selectedLocation);
      }

      const statsParams = new URLSearchParams();
      if (selectedLocation) {
        statsParams.append('location_id', selectedLocation);
      }
      if (currentUser?.login) {
        statsParams.append('user_login', currentUser.login);
      }

      const [tasksRes, groupsRes, statsRes] = await Promise.all([
        api.get(`/tasks?${params}`),
        api.get(`/tasks/groups?${groupParams}`),
        api.get(`/tasks/stats?${statsParams}`)
      ]);

      if (tasksRes.data.success) setTasks(tasksRes.data.data || []);
      if (groupsRes.data.success) setGroups(groupsRes.data.data || []);
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (error) {
      console.error('TasksPage: Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedGroup, selectedStatus, searchQuery, selectedLocation, currentUser, showAllTasks]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Task actions
  const toggleTask = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/toggle`);
      fetchData();
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Czy na pewno usunąć to zadanie?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingTask(null);
  };

  return {
    // Data
    tasks,
    groups,
    stats,
    loading,
    locations,
    users,
    currentUser,

    // Filters
    selectedCategory,
    setSelectedCategory,
    selectedGroup,
    setSelectedGroup,
    selectedStatus,
    setSelectedStatus,
    searchQuery,
    setSearchQuery,
    selectedLocation,
    setSelectedLocation,
    showAllTasks,
    setShowAllTasks,

    // Modals
    showAddModal,
    setShowAddModal,
    showQuickTaskModal,
    setShowQuickTaskModal,
    showEditModal,
    editingTask,
    openEditModal,
    closeEditModal,

    // Actions
    fetchData,
    toggleTask,
    deleteTask
  };
}
